import React, { useState, useEffect, useRef } from 'react';
import QRCode from 'react-qr-code';
import { Html5Qrcode } from 'html5-qrcode';
import {
  Camera, WarningCircle, Keyboard, HourglassMedium, CheckCircle,
  Prohibit, ArrowLeft, Siren,
} from '@phosphor-icons/react';
import Layout from '../../components/Layout';
import { useAuth } from '../../context/AuthContext';
import securityService from '../../services/securityService';
import './security.css';

const QR_READER_ELEMENT_ID = 'verify-qr-reader';

const initials = (name) =>
  name ? name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase() : '?';

const STEPS = {
  SELECT_STUDENT: 'select_student',
  ENTER_TOKEN:    'enter_token',
  VERIFYING:      'verifying',
  RESULT:         'result',
};

export default function QRVerificationPage() {
  const { user } = useAuth();

  const [step, setStep]             = useState(STEPS.SELECT_STUDENT);
  const [students, setStudents]     = useState([]);
  const [loadingStudents, setLoadingStudents] = useState(true);
  const [loadError, setLoadError]   = useState(null);
  const [search, setSearch]         = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [tokenInput, setTokenInput] = useState('');
  const [result, setResult]         = useState(null);
  const [pickupPersons, setPickupPersons] = useState([]);
  const [scanMode, setScanMode]     = useState('camera'); // 'camera' | 'manual'
  const [cameraError, setCameraError] = useState(null);
  const tokenRef = useRef(null);

  const collectorShareUrl = (token) =>
    `${window.location.origin}/security/collector-share?token=${token}`;

  // Load pending/awaiting students
  const loadStudents = React.useCallback(() => {
    if (!user?.school_id) return;
    setLoadingStudents(true);
    setLoadError(null);
    securityService.listStudents(user.school_id)
      .then(data => {
        const all = Array.isArray(data) ? data : [];
        // Officer only sees students who haven't been released yet
        const pending = all.filter(s =>
          !['en_route_collector', 'en_route_bus', 'safe_confirmed'].includes(s.arrival_status)
        );
        setStudents(pending);
      })
      .catch(() => setLoadError('Could not load students. Check your connection.'))
      .finally(() => setLoadingStudents(false));
  }, [user?.school_id]);

  useEffect(() => { loadStudents(); }, [user?.school_id, loadStudents]);

  // Reference list for the officer to visually check the collector's ID against
  useEffect(() => {
    if (step !== STEPS.RESULT || !selectedStudent) { setPickupPersons([]); return; }
    securityService.listPickupPersons(selectedStudent.id)
      .then(data => setPickupPersons((Array.isArray(data) ? data : []).filter(p => p.is_active)))
      .catch(() => setPickupPersons([]));
  }, [step, selectedStudent]);

  // Focus token input when entering that step
  useEffect(() => {
    if (step === STEPS.ENTER_TOKEN && tokenRef.current) {
      setTimeout(() => tokenRef.current?.focus(), 100);
    }
  }, [step]);

  const handleSelectStudent = (student) => {
    setSelectedStudent(student);
    setTokenInput('');
    setResult(null);
    setScanMode('camera');
    setCameraError(null);
    setStep(STEPS.ENTER_TOKEN);
  };

  const handleVerify = async (tokenOverride) => {
    const tokenValue = (tokenOverride ?? tokenInput).trim();
    if (!tokenValue) return;
    setTokenInput(tokenValue);
    setStep(STEPS.VERIFYING);

    let gateLat = null;
    let gateLng = null;
    try {
      const pos = await new Promise((res, rej) =>
        navigator.geolocation.getCurrentPosition(res, rej, { timeout: 3000 })
      );
      gateLat = pos.coords.latitude;
      gateLng = pos.coords.longitude;
    } catch (_) { /* geolocation optional */ }

    try {
      const data = await securityService.verifyQr(tokenValue, gateLat, gateLng);
      setResult(data);
    } catch (err) {
      setResult({ result: 'unauthorized', reason: err.message });
    }
    setStep(STEPS.RESULT);
  };

  // Drive the camera scanner whenever step 2 is in camera mode
  useEffect(() => {
    if (step !== STEPS.ENTER_TOKEN || scanMode !== 'camera') return;

    let isActive = true;
    let started = false;
    const html5Qr = new Html5Qrcode(QR_READER_ELEMENT_ID);

    html5Qr.start(
      { facingMode: 'environment' },
      { fps: 10, qrbox: 220 },
      (decodedText) => {
        if (!isActive) return;
        isActive = false;
        if (started) html5Qr.stop().catch(() => {});
        handleVerify(decodedText);
      },
      () => { /* per-frame scan miss — ignore */ }
    ).then(() => {
      started = true;
    }).catch((err) => {
      if (!isActive) return;
      setScanMode('manual');
      setCameraError('Camera unavailable — enter the token manually.');
      console.warn('QR camera start failed:', err);
    });

    return () => {
      isActive = false;
      // html5-qrcode throws synchronously (not a rejected promise) if stop()
      // is called on a scanner whose start() never resolved — e.g. camera
      // permission denied. That escapes this .catch() and, uncaught inside
      // an effect cleanup, was crashing the whole page (no error boundary
      // wraps it). Only call stop() once start() has actually succeeded.
      if (started) {
        html5Qr.stop().then(() => html5Qr.clear()).catch(() => {});
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, scanMode, selectedStudent?.id]);

  const resetToStudentSelect = () => {
    setStep(STEPS.SELECT_STUDENT);
    setSelectedStudent(null);
    setTokenInput('');
    setResult(null);
    setScanMode('camera');
    setCameraError(null);
    loadStudents();
  };

  const filteredStudents = students.filter(s => {
    const q = search.toLowerCase();
    return (
      s.name?.toLowerCase().includes(q) ||
      s.class?.toLowerCase().includes(q) ||
      s.ghana_post_address?.toLowerCase().includes(q)
    );
  });

  const authorized = result?.result === 'authorized';

  return (
    <Layout>
      <div className="verify-page">

        {/* ── Step 1: Select Student ─────────────────────────────────────── */}
        {step === STEPS.SELECT_STUDENT && (
          <div className="verify-card" style={{ maxWidth: 560 }}>
            <div style={{
              background: '#16a34a', padding: '20px 24px',
              borderRadius: '20px 20px 0 0',
            }}>
              <p style={{ color: 'white', fontWeight: 800, fontSize: 18, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Camera size={18} /> QR Verification
              </p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: 0 }}>
                Step 1 of 2 — Select the student being collected
              </p>
            </div>

            <div style={{ padding: 20 }}>
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="Search by name or class..."
                autoFocus
                style={{
                  width: '100%', boxSizing: 'border-box', padding: '10px 14px',
                  borderRadius: 10, border: '1px solid #d1fae5', fontSize: 13,
                  outline: 'none', background: '#f8fafc', marginBottom: 16,
                }}
              />

              {loadError && (
                <div style={{
                  background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8,
                  padding: 12, fontSize: 13, color: '#dc2626', marginBottom: 12,
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <WarningCircle size={16} /> {loadError}
                  <button onClick={loadStudents} style={{
                    marginLeft: 8, textDecoration: 'underline', background: 'none',
                    border: 'none', color: '#dc2626', cursor: 'pointer', fontSize: 13,
                  }}>Retry</button>
                </div>
              )}

              {loadingStudents && (
                <div style={{ textAlign: 'center', padding: 32, color: '#6b7280', fontSize: 13 }}>
                  Loading students...
                </div>
              )}

              {!loadingStudents && filteredStudents.length === 0 && (
                <div style={{ textAlign: 'center', padding: 32, color: '#6b7280', fontSize: 13 }}>
                  {search ? 'No students match your search.' : 'All students have been released today.'}
                </div>
              )}

              <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 400, overflowY: 'auto' }}>
                {filteredStudents.map(student => (
                  <button
                    key={student.id}
                    onClick={() => handleSelectStudent(student)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 12,
                      padding: '12px 14px', borderRadius: 12,
                      border: '1px solid #e5e7eb', background: 'white',
                      cursor: 'pointer', textAlign: 'left',
                    }}
                  >
                    <div
                      className={`sec-avatar ${student.gender}`}
                      style={{ width: 40, height: 40, flexShrink: 0 }}
                    >
                      {initials(student.name)}
                    </div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <p style={{ margin: 0, fontWeight: 700, color: '#111827', fontSize: 14 }}>
                        {student.name}
                      </p>
                      <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                        {student.class}
                        {student.ghana_post_address && ` · ${student.ghana_post_address}`}
                      </p>
                    </div>
                    <span style={{
                      fontSize: 11, fontWeight: 600, padding: '3px 10px',
                      borderRadius: 20, background: '#fef3c7', color: '#92400e',
                      flexShrink: 0,
                    }}>
                      Awaiting
                    </span>
                    <span style={{ color: '#16a34a', fontSize: 18 }}>›</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ── Step 2: Enter Token ────────────────────────────────────────── */}
        {step === STEPS.ENTER_TOKEN && selectedStudent && (
          <div className="verify-card">
            <div style={{
              background: '#16a34a', padding: '20px 24px',
              borderRadius: '20px 20px 0 0',
            }}>
              <p style={{ color: 'white', fontWeight: 800, fontSize: 18, margin: '0 0 4px', display: 'flex', alignItems: 'center', gap: 8 }}>
                <Camera size={18} /> QR Verification
              </p>
              <p style={{ color: 'rgba(255,255,255,0.75)', fontSize: 13, margin: 0 }}>
                Step 2 of 2 — Scan or enter the QR token
              </p>
            </div>

            <div style={{ padding: 20 }}>
              {/* Selected student summary */}
              <div style={{
                background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12,
                padding: 14, marginBottom: 20, display: 'flex', alignItems: 'center', gap: 12,
              }}>
                <div className={`sec-avatar ${selectedStudent.gender}`}
                  style={{ width: 44, height: 44, flexShrink: 0 }}>
                  {initials(selectedStudent.name)}
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
                    {selectedStudent.name}
                  </p>
                  <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                    {selectedStudent.class}
                    {selectedStudent.ghana_post_address && ` · ${selectedStudent.ghana_post_address}`}
                  </p>
                </div>
                <button
                  onClick={() => setStep(STEPS.SELECT_STUDENT)}
                  style={{
                    background: 'none', border: '1px solid #d1fae5',
                    borderRadius: 8, padding: '5px 10px', fontSize: 12, color: '#16a34a',
                    cursor: 'pointer',
                  }}
                >
                  Change
                </button>
              </div>

              {scanMode === 'camera' ? (
                <>
                  <div
                    id={QR_READER_ELEMENT_ID}
                    style={{
                      width: '100%', minHeight: 240, borderRadius: 12,
                      overflow: 'hidden', background: '#0f172a', marginBottom: 10,
                    }}
                  />
                  <p style={{ fontSize: 11, color: '#6b7280', textAlign: 'center', margin: '0 0 16px' }}>
                    Point the camera at the collector's QR code
                  </p>
                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => setScanMode('manual')}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 10, border: '1px solid #e5e7eb',
                        background: 'white', color: '#111827', fontWeight: 600, fontSize: 14,
                        cursor: 'pointer',
                      }}
                    >
                      <Keyboard size={14} /> Type the token instead
                    </button>
                    <button
                      onClick={() => setStep(STEPS.SELECT_STUDENT)}
                      style={{
                        padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
                        background: 'white', color: '#6b7280', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      Back
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {cameraError && (
                    <div style={{ fontSize: 12, color: '#dc2626', marginBottom: 8 }}>
                      {cameraError}
                    </div>
                  )}
                  <label style={{ fontSize: 12, color: '#6b7280', display: 'block', marginBottom: 6 }}>
                    Enter or paste the QR token from the collector's phone:
                  </label>
                  <input
                    ref={tokenRef}
                    type="text"
                    value={tokenInput}
                    onChange={e => setTokenInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && handleVerify()}
                    placeholder="e.g. 3f8a2b1c-4d5e-6789-abcd-..."
                    autoFocus
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '11px 14px',
                      borderRadius: 10, border: '1px solid #d1fae5', fontSize: 13,
                      fontFamily: 'monospace', outline: 'none', background: '#f8fafc',
                      color: '#111827', marginBottom: 10,
                    }}
                  />
                  <button
                    onClick={() => { setScanMode('camera'); setCameraError(null); }}
                    style={{
                      width: '100%', padding: '10px', borderRadius: 10, border: '1px solid #e5e7eb',
                      background: 'white', color: '#111827', fontWeight: 600, fontSize: 13,
                      cursor: 'pointer', marginBottom: 16,
                    }}
                  >
                    <Camera size={14} /> Use camera instead
                  </button>

                  <div style={{ display: 'flex', gap: 10 }}>
                    <button
                      onClick={() => handleVerify()}
                      disabled={!tokenInput.trim()}
                      style={{
                        flex: 1, padding: '12px', borderRadius: 10, border: 'none',
                        background: tokenInput.trim() ? '#16a34a' : '#d1d5db',
                        color: 'white', fontWeight: 700, fontSize: 15,
                        cursor: tokenInput.trim() ? 'pointer' : 'not-allowed',
                      }}
                    >
                      <CheckCircle size={15} /> Verify QR Code
                    </button>
                    <button
                      onClick={() => setStep(STEPS.SELECT_STUDENT)}
                      style={{
                        padding: '12px 16px', borderRadius: 10, border: '1px solid #e5e7eb',
                        background: 'white', color: '#6b7280', cursor: 'pointer', fontSize: 13,
                      }}
                    >
                      Back
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* ── Verifying ────────────────────────────────────────────────── */}
        {step === STEPS.VERIFYING && (
          <div className="verify-card">
            <div style={{ padding: 48, textAlign: 'center' }}>
              <div style={{ fontSize: 48, marginBottom: 16 }}><HourglassMedium size={48} /></div>
              <p style={{ fontWeight: 700, color: '#111827', margin: '0 0 8px' }}>
                Verifying QR code...
              </p>
              <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>
                Checking authorization for {selectedStudent?.name}
              </p>
            </div>
          </div>
        )}

        {/* ── Step 3: Result ────────────────────────────────────────────── */}
        {step === STEPS.RESULT && result && (
          <div className="verify-card">
            <div className={`verify-result-banner ${authorized ? 'success' : 'failure'}`}>
              <div className="verify-result-icon">{authorized ? <CheckCircle size={40} weight="fill" /> : <Prohibit size={40} weight="fill" />}</div>
              <p className="verify-result-title">{authorized ? 'AUTHORIZED' : 'UNAUTHORIZED'}</p>
              <p className="verify-result-sub">
                {authorized
                  ? `${selectedStudent?.name} may be released`
                  : result.reason || 'Token is invalid, expired, or already used'}
              </p>
            </div>

            <div className="verify-body">
              {/* Student info recap */}
              {selectedStudent && (
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '14px 0', borderBottom: '1px solid #f3f4f6', marginBottom: 16,
                }}>
                  <div className={`sec-avatar ${selectedStudent.gender}`}
                    style={{ width: 44, height: 44 }}>
                    {initials(selectedStudent.name)}
                  </div>
                  <div>
                    <p style={{ margin: 0, fontWeight: 700, color: '#111827' }}>
                      {selectedStudent.name}
                    </p>
                    <p style={{ margin: 0, fontSize: 12, color: '#6b7280' }}>
                      {selectedStudent.class}
                    </p>
                  </div>
                </div>
              )}

              {/* Authorized pickup persons on file — check the collector's ID against this */}
              {authorized && pickupPersons.length > 0 && (
                <div style={{
                  background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12,
                  padding: 14, marginBottom: 16,
                }}>
                  <p style={{ fontSize: 12, fontWeight: 700, color: '#92400e', margin: '0 0 8px' }}>
                    Check ID against approved pickup persons:
                  </p>
                  {pickupPersons.map(p => (
                    <div key={p.id} style={{ fontSize: 12, color: '#451a03', padding: '3px 0' }}>
                      <strong>{p.name}</strong>{p.relationship ? ` (${p.relationship})` : ''}{p.phone ? ` — ${p.phone}` : ''}
                      {p.notes && <div style={{ fontSize: 11, color: '#92400e' }}>{p.notes}</div>}
                    </div>
                  ))}
                </div>
              )}

              {/* Authorized: show collector QR */}
              {authorized && result.collector_session_token && (
                <div style={{ marginBottom: 20 }}>
                  <p style={{ fontWeight: 700, fontSize: 13, color: '#111827', marginBottom: 10 }}>
                    Ask the collector to scan this with their phone:
                  </p>
                  <div style={{
                    background: 'white', border: '1px solid #e5e7eb',
                    borderRadius: 16, padding: 20, textAlign: 'center',
                  }}>
                    <QRCode
                      value={collectorShareUrl(result.collector_session_token)}
                      size={180}
                      level="H"
                    />
                    <p style={{ fontSize: 11, color: '#9ca3af', margin: '12px 0 0' }}>
                      Opens live GPS sharing on the collector's phone —<br />
                      the parent will see their location in real time
                    </p>
                  </div>
                </div>
              )}

              {/* Unauthorized: alert */}
              {!authorized && (
                <div style={{
                  background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 12,
                  padding: 14, marginBottom: 16, fontSize: 13, color: '#dc2626',
                  display: 'flex', alignItems: 'center', gap: 6,
                }}>
                  <WarningCircle size={16} /> Do not release the student. Log this unauthorized attempt if needed.
                </div>
              )}

              <div style={{ display: 'flex', gap: 10 }}>
                {authorized ? (
                  <button
                    onClick={resetToStudentSelect}
                    style={{
                      flex: 1, padding: 12, borderRadius: 10, border: 'none',
                      background: '#16a34a', color: 'white', fontWeight: 700,
                      fontSize: 14, cursor: 'pointer',
                    }}
                  >
                    <CheckCircle size={15} /> Done — Next Student
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => setStep(STEPS.ENTER_TOKEN)}
                      style={{
                        flex: 1, padding: 12, borderRadius: 10, border: '1px solid #e5e7eb',
                        background: 'white', color: '#111827', fontWeight: 600,
                        fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      Try Again
                    </button>
                    <button
                      onClick={resetToStudentSelect}
                      style={{
                        flex: 1, padding: 12, borderRadius: 10, border: 'none',
                        background: '#dc2626', color: 'white', fontWeight: 700,
                        fontSize: 14, cursor: 'pointer',
                      }}
                    >
                      <Siren size={15} /> Alert Security
                    </button>
                  </>
                )}
              </div>

              <button
                onClick={resetToStudentSelect}
                style={{
                  width: '100%', marginTop: 10, padding: '10px', borderRadius: 10,
                  border: '1px solid #e5e7eb', background: 'white', color: '#6b7280',
                  fontSize: 13, cursor: 'pointer',
                }}
              >
                <ArrowLeft size={14} /> Scan Another Student
              </button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
